import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { brandColors, ENYCGAMES_LOGO_URL } from '@config/brandTokens';
import { useAuthStore } from '@store/authStore';
import {
  confirmStripeCheckout,
  fetchPaymentGateways,
  fetchPaymentHistory,
  fetchPaymentMethods,
  purchaseSubscription,
  savePaymentMethod,
} from '@services/subscriptionService';
import {
  earlyBirdUsuallyLine,
  familyPlanSummary,
  inAppDashboardPath,
  inAppInvoicesPath,
  invoiceStripeStatus,
  paymentGatewayPath,
  preferredPaymentGateway,
} from '@/content/subscriptionCatalog';
import { formatCatalogYearly } from '@/content/subscriptionPricing';
import { formatInvoiceAmount, formatUtcDate } from '@/content/billingFormat';
import {
  clearSubscriptionCheckoutDraft,
  readSubscriptionCheckoutDraft,
  writeSubscriptionCheckoutDraft,
} from '@/utils/subscriptionCheckout';
import PaymentGatewayChooser from '@components/billing/PaymentGatewayChooser';
import { isSafeAppReturnUrl } from '@utils/safeReturnUrl';
import type { BillingInvoice, PaymentGateway, PaymentMethodOnFile } from '@types/index';
import RaisePaymentIssueDialog from '@components/billing/RaisePaymentIssueDialog';

const GATEWAY_THEME: Record<
  PaymentGateway,
  { name: string; accent: string; ink: string; card: string; blurb: string; payBlurb: string }
> = {
  stripe: {
    name: 'Stripe',
    accent: '#635BFF',
    ink: '#0A2540',
    card: '#F6F9FC',
    blurb: 'Add or update the card EncycGames uses for yearly Stripe charges.',
    payBlurb: 'Review your EncycGames yearly plan, then continue to Stripe checkout.',
  },
  razorpay: {
    name: 'Razorpay',
    accent: '#072654',
    ink: '#072654',
    card: '#F4F7FB',
    blurb: 'Add or update the card EncycGames uses for yearly Razorpay charges.',
    payBlurb: 'Review your EncycGames yearly plan, then continue to Razorpay checkout.',
  },
};

const CARD_BRANDS = ['Visa', 'Mastercard', 'American Express', 'RuPay', 'Other'];

const PaymentGatewayPage: React.FC = () => {
  const { gateway: gatewayParam } = useParams<{ gateway: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, setUser } = useAuthStore();
  const gateway: PaymentGateway = gatewayParam === 'razorpay' ? 'razorpay' : 'stripe';
  const theme = GATEWAY_THEME[gateway];
  const role = String(user?.role || '');
  const draft = useMemo(() => readSubscriptionCheckoutDraft(), [gateway, user?.id]);
  const purchaseAmount = Number(searchParams.get('amount') || draft?.amount || '');
  const purchasePlan = String(searchParams.get('plan') || draft?.planLabel || '').trim();
  const requestedReturn = String(searchParams.get('returnUrl') || '').trim();
  const returnUrl =
    isSafeAppReturnUrl(requestedReturn) ? requestedReturn : inAppDashboardPath(role);
  const isPurchase = Boolean(searchParams.get('amount') || searchParams.get('plan'));
  const planSummary = (draft?.family ? familyPlanSummary(draft.family) : draft?.summary) || purchasePlan;
  const displayAmount =
    Number.isFinite(purchaseAmount) && purchaseAmount > 0
      ? formatCatalogYearly(purchaseAmount, draft?.currency || searchParams.get('currency') || 'USD')
      : null;
  const displayCurrency = draft?.currency || searchParams.get('currency') || 'USD';
  const displayList =
    draft?.earlyBirdActive && draft.listUsd != null
      ? formatCatalogYearly(draft.listUsd, displayCurrency)
      : null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [payError, setPayError] = useState('');
  const [saved, setSaved] = useState('');
  const [methods, setMethods] = useState<PaymentMethodOnFile[]>([]);
  const [history, setHistory] = useState<BillingInvoice[]>([]);
  const [gateways, setGateways] = useState({ stripe: false, razorpay: false });
  const [issueOpen, setIssueOpen] = useState(false);
  const [holderName, setHolderName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [cvc, setCvc] = useState('');
  const [brand, setBrand] = useState(CARD_BRANDS[0]);
  const providerReady = gateways[gateway];
  const suggestedGateway = preferredPaymentGateway(
    draft?.currency || searchParams.get('currency'),
  );

  const switchGateway = (next: PaymentGateway) => {
    if (next === gateway) return;
    const existing = readSubscriptionCheckoutDraft();
    if (existing) {
      writeSubscriptionCheckoutDraft({ ...existing, provider: next });
    }
    const params = new URLSearchParams(searchParams);
    navigate(`${paymentGatewayPath(role, next)}?${params.toString()}`, { replace: true });
  };

  const onFile = useMemo(
    () => methods.find((method) => method.gateway === gateway) || methods[0],
    [methods, gateway],
  );

  const load = async () => {
    const [cards, rows, configured] = await Promise.all([
      fetchPaymentMethods(),
      fetchPaymentHistory(gateway),
      fetchPaymentGateways(),
    ]);
    setMethods(cards.filter((card) => card.gateway === gateway));
    setHistory(rows);
    setGateways(configured);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateway, user?.id]);

  useEffect(() => {
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    if (!payment) return;
    let cancelled = false;
    (async () => {
      if (payment === 'success' && sessionId) {
        try {
          const paid = await confirmStripeCheckout(sessionId);
          if (cancelled) return;
          clearSubscriptionCheckoutDraft();
          if (user) setUser({ ...user, subscription: paid });
          navigate(returnUrl, { replace: true });
          return;
        } catch (err: unknown) {
          const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
          if (!cancelled) {
            setPayError(anyErr.response?.data?.message || anyErr.message || 'Could not confirm payment');
          }
        }
      }
      const next = new URLSearchParams(searchParams);
      next.delete('payment');
      next.delete('session_id');
      setSearchParams(next, { replace: true });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('payment'), searchParams.get('session_id')]);

  const handlePay = async () => {
    if (!providerReady) {
      setPayError('Payment provider not connected yet');
      return;
    }
    setPaying(true);
    setPayError('');
    try {
      const offerId = draft?.offerId || 'players_parents';
      const paid = await purchaseSubscription(offerId, draft?.family, 'purchase', { provider: gateway });
      if (paid.status === 'pending_payment' || paid.status === 'pending') {
        return;
      }
      if (paid.status === 'active' || paid.status === 'beta') {
        clearSubscriptionCheckoutDraft();
        if (user) setUser({ ...user, subscription: paid });
        navigate(returnUrl);
      }
    } catch (err: unknown) {
      const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
      setPayError(anyErr.response?.data?.message || anyErr.message || 'Could not start payment');
    } finally {
      setPaying(false);
    }
  };

  const submitCard = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSaved('');
    try {
      const digits = cardNumber.replace(/\D/g, '');
      await savePaymentMethod({
        gateway,
        brand,
        cardNumber: digits,
        last4: digits.slice(-4),
        expMonth: Number(expMonth),
        expYear: Number(expYear),
        holderName: holderName.trim(),
      });
      setCvc('');
      setCardNumber('');
      setSaved('Card on file updated.');
      await load();
    } catch (err: unknown) {
      const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
      setError(anyErr.response?.data?.message || anyErr.message || 'Could not save card');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.card, py: { xs: 3, md: 5 }, px: 2 }}>
      <Box sx={{ maxWidth: 760, mx: 'auto' }}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2.5 }}>
          <Box
            component="img"
            src={ENYCGAMES_LOGO_URL}
            alt="EncycGames"
            sx={{ height: 36, width: 'auto' }}
          />
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: theme.accent }} />
          <Typography
            sx={{
              fontFamily: '"Sora", Arial, sans-serif',
              fontWeight: 800,
              fontSize: '1.35rem',
              color: theme.ink,
            }}
          >
            EncycGames · {theme.name}
          </Typography>
        </Stack>

        <Button
          onClick={() => navigate(returnUrl)}
          sx={{
            mb: 2.5,
            textTransform: 'none',
            fontWeight: 800,
            color: theme.ink,
            bgcolor: brandColors.white,
            border: '1px solid #dbe3ef',
            px: 1.5,
            '&:hover': { bgcolor: '#eef2f7' },
          }}
        >
          Return to EncycGames dashboard
        </Button>

        {isPurchase && (
          <Box
            sx={{
              mb: 2,
              p: { xs: 2, md: 2.5 },
              borderRadius: 2,
              bgcolor: brandColors.white,
              border: `1px solid ${theme.accent}33`,
            }}
          >
            <Typography sx={{ fontWeight: 800, color: theme.ink, mb: 0.35 }}>Yearly plan</Typography>
            <Typography variant="body2" sx={{ color: brandColors.slate, mb: 1.5 }}>
              {theme.payBlurb}
            </Typography>
            {displayAmount && (
              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.5 }}>
                <Typography
                  sx={{
                    fontFamily: '"Sora", Arial, sans-serif',
                    fontWeight: 900,
                    fontSize: '2rem',
                    color: theme.ink,
                    lineHeight: 1,
                  }}
                >
                  {displayAmount}
                </Typography>
                <Typography sx={{ color: brandColors.slate, fontWeight: 600 }}>/ year</Typography>
              </Stack>
            )}
            {displayList && draft?.discountPercent != null && (
              <Typography
                variant="body2"
                sx={{ color: brandColors.slate, fontSize: '0.85rem', fontWeight: 600, mb: 0.5 }}
              >
                {earlyBirdUsuallyLine(draft.discountPercent, displayList)}
              </Typography>
            )}
            {planSummary && (
              <Typography variant="body2" sx={{ color: brandColors.slate, mb: (draft?.family?.extraChildren || 0) > 0 ? 0.35 : 1.5 }}>
                {planSummary}
              </Typography>
            )}
            {(draft?.family?.extraChildren || 0) > 0 ? (
              <Typography
                variant="caption"
                sx={{ color: brandColors.slate, display: 'block', mb: 1.5, fontWeight: 600 }}
              >
                + {draft.family?.extraChildren} extra{' '}
                {draft.family?.extraChildren === 1 ? 'child' : 'children'} included
              </Typography>
            ) : null}
            <Box sx={{ mt: 1.75, mb: 0.5 }}>
              <PaymentGatewayChooser
                value={gateway}
                suggested={suggestedGateway}
                onChange={switchGateway}
              />
            </Box>
            {!loading && !providerReady && (
              <Alert severity="warning" sx={{ mt: 1.5, mb: 1.5 }}>
                Payment provider not connected yet. EncycGames has not connected {theme.name} on this
                server. Your yearly plan will not activate until payment succeeds.
              </Alert>
            )}
            {payError && (
              <Alert severity="error" sx={{ mt: 1.5, mb: 1.5 }}>
                {payError}
              </Alert>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
              <Button
                variant="contained"
                disabled={paying || loading || !providerReady}
                onClick={handlePay}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  bgcolor: theme.accent,
                  '&:hover': { bgcolor: theme.ink },
                }}
              >
                {paying ? 'Opening checkout…' : providerReady ? `Pay with ${theme.name}` : 'Pay'}
              </Button>
              <Button
                onClick={() => navigate(returnUrl)}
                sx={{ textTransform: 'none', fontWeight: 800, color: theme.ink }}
              >
                Continue
              </Button>
            </Stack>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={2}>
            <Box
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 2,
                bgcolor: brandColors.white,
                border: '1px solid #e2e8f0',
              }}
            >
              <Typography sx={{ fontWeight: 800, color: theme.ink, mb: 0.5 }}>Card on file</Typography>
              <Typography variant="body2" sx={{ color: brandColors.slate, mb: 1.75 }}>
                {theme.blurb}
              </Typography>
              {onFile ? (
                <Typography sx={{ fontWeight: 800, color: theme.ink, mb: 1.5 }}>
                  {onFile.brand} · •••• {onFile.last4} · {String(onFile.expMonth).padStart(2, '0')}/
                  {onFile.expYear}
                  {onFile.holderName ? ` · ${onFile.holderName}` : ''}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ color: brandColors.slate, mb: 1.5 }}>
                  No card stored for {theme.name} yet.
                </Typography>
              )}

              <Box component="form" onSubmit={submitCard}>
                <Stack spacing={1.25}>
                  <TextField
                    label="Name on card"
                    value={holderName}
                    onChange={(e) => setHolderName(e.target.value)}
                    size="small"
                    fullWidth
                    required
                  />
                  <TextField
                    select
                    label="Brand"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    size="small"
                    fullWidth
                  >
                    {CARD_BRANDS.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Card number"
                    value={cardNumber}
                    onChange={(e) => setCardNumber(e.target.value)}
                    size="small"
                    fullWidth
                    required
                    inputProps={{ inputMode: 'numeric', autoComplete: 'cc-number' }}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <TextField
                      label="MM"
                      value={expMonth}
                      onChange={(e) => setExpMonth(e.target.value.replace(/\D/g, '').slice(0, 2))}
                      size="small"
                      required
                      sx={{ width: { sm: 90 } }}
                    />
                    <TextField
                      label="YYYY"
                      value={expYear}
                      onChange={(e) => setExpYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      size="small"
                      required
                      sx={{ width: { sm: 110 } }}
                    />
                    <TextField
                      label="CVC"
                      value={cvc}
                      onChange={(e) => setCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      size="small"
                      type="password"
                      required
                      sx={{ width: { sm: 110 } }}
                    />
                  </Stack>
                  {error && <Alert severity="error">{error}</Alert>}
                  {saved && <Alert severity="success">{saved}</Alert>}
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving}
                    sx={{
                      alignSelf: 'flex-start',
                      textTransform: 'none',
                      fontWeight: 800,
                      bgcolor: theme.accent,
                      '&:hover': { bgcolor: theme.ink },
                    }}
                  >
                    {saving ? 'Saving…' : onFile ? 'Update card' : 'Save card'}
                  </Button>
                </Stack>
              </Box>
            </Box>

            <Box
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 2,
                bgcolor: brandColors.white,
                border: '1px solid #e2e8f0',
              }}
            >
              <Typography sx={{ fontWeight: 800, color: theme.ink, mb: 0.35 }}>
                {theme.name} purchase history
              </Typography>
              <Typography variant="caption" sx={{ color: brandColors.slate, fontWeight: 700 }}>
                UTC
              </Typography>
              <Box
                sx={{
                  display: { xs: 'none', sm: 'grid' },
                  gridTemplateColumns: '1.1fr 1.6fr 0.7fr 1fr',
                  gap: 1,
                  px: 1.25,
                  pt: 1.5,
                  pb: 0.75,
                }}
              >
                {['Date (UTC)', 'Description', 'Status', 'Amount'].map((label) => (
                  <Typography key={label} variant="caption" sx={{ color: brandColors.slate, fontWeight: 800 }}>
                    {label}
                  </Typography>
                ))}
              </Box>
              {history.length === 0 ? (
                <Box
                  sx={{
                    px: 1.25,
                    py: 1.25,
                    border: '1px solid #e2e8f0',
                    borderRadius: 1.5,
                    bgcolor: theme.card,
                  }}
                >
                  <Typography sx={{ color: '#94a3b8', fontWeight: 700, fontSize: '0.85rem' }}>—</Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {history.map((invoice) => (
                    <Box
                      key={invoice.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1.1fr 1.6fr 0.7fr 1fr' },
                        gap: 1,
                        px: 1.25,
                        py: 1.1,
                        border: '1px solid #e2e8f0',
                        borderRadius: 1.5,
                        bgcolor: theme.card,
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: theme.ink }}>
                        {formatUtcDate(invoice.date)}
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: theme.ink }}>
                        {invoice.description || invoice.planLabel || ''}
                      </Typography>
                      <Typography sx={{ fontSize: '0.85rem', color: brandColors.slate }}>
                        {invoice.invoiceStatus || invoiceStripeStatus(invoice.status, invoice.kind)}
                      </Typography>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: theme.ink }}>
                        {formatInvoiceAmount(invoice.amountUsd, invoice.currency)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Button
                onClick={() => navigate(inAppInvoicesPath(role))}
                sx={{ textTransform: 'none', fontWeight: 800, color: theme.ink }}
              >
                Billing & invoices
              </Button>
              <Button
                onClick={() => setIssueOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 800, color: theme.ink }}
              >
                Raise issue
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>
      <RaisePaymentIssueDialog open={issueOpen} onClose={() => setIssueOpen(false)} gateway={gateway} />
    </Box>
  );
};

export default PaymentGatewayPage;
